import { useEffect } from 'react';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

import HomePage            from './pages/HomePage.jsx';
import LoginPage           from './pages/LoginPage.jsx';
import SignupPage          from './pages/SignupPage.jsx';
import ForgotPasswordPage  from './pages/ForgotPasswordPage.jsx';
import ResetPasswordPage   from './pages/ResetPasswordPage.jsx';
import TerritoriesPage     from './pages/TerritoriesPage.jsx';
import BriefingPage    from './pages/BriefingPage.jsx';
import MyLeadsPage     from './pages/MyLeadsPage.jsx';
import LettersPage     from './pages/LettersPage.jsx';
import ProfilePage     from './pages/ProfilePage.jsx';
import VoiceOnboardingPage from './pages/VoiceOnboardingPage.jsx';
import NetworkPage from './pages/NetworkPage.jsx';
import NetworkJoinPage from './pages/NetworkJoinPage.jsx';
import { PrivacyPage, TermsPage } from './pages/LegalPages.jsx';

import AuthGate from './components/shell/AuthGate.jsx';
import { useAuth } from './lib/AuthContext.jsx';

// Application routes for the V3 React SPA.
//
// Public routes (no auth):
//   /                — marketing landing page
//   /login           — email + password sign-in, with magic-link fallback
//   /signup          — email + password 'request access'
//   /forgot-password — request a password-reset email
//   /reset-password  — landing page from reset email; sets new password
//   /privacy         — privacy policy
//   /terms           — terms of service
//
// Authenticated routes (gated via <AuthGate>):
//   /territories — agent's territory list
//   /zip/:zip    — briefing (operator cards, tiered map, dossier)
//   /profile     — agent profile form
//
// Legacy alias: /coverage redirects to /territories.
//
// AuthGate handles the redirect-to-login flow, including preserving
// the original path as ?next= so post-sign-in the user lands where
// they were trying to go.
//
// Inside protected pages, components call useAuth() directly to pull
// the current profile + signOut handler. App.jsx no longer threads
// these as explicit props — the context is the source of truth.
export default function App() {
  return (
    <ErrorBoundary>
    <VoiceOnboardingNudge />
    <Routes>
      {/* Public marketing + auth */}
      <Route path="/"                element={<HomePage           />} />
      <Route path="/login"           element={<LoginPage          />} />
      <Route path="/signup"          element={<SignupPage         />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password"  element={<ResetPasswordPage  />} />
      <Route path="/privacy"         element={<PrivacyPage        />} />
      <Route path="/terms"           element={<TermsPage          />} />

      {/* Authenticated app surfaces */}
      <Route
        path="/territories"
        element={
          <AuthGate>
            <AuthenticatedTerritories />
          </AuthGate>
        }
      />
      <Route
        path="/zip/:zip"
        element={
          <AuthGate>
            <AuthenticatedBriefing />
          </AuthGate>
        }
      />
      <Route
        path="/my-leads"
        element={
          <AuthGate>
            <MyLeadsPage />
          </AuthGate>
        }
      />
      <Route
        path="/letters"
        element={
          <AuthGate>
            <LettersPage />
          </AuthGate>
        }
      />
      <Route
        path="/profile"
        element={
          <AuthGate>
            <ProfilePage />
          </AuthGate>
        }
      />
      <Route
        path="/profile/voice"
        element={
          <AuthGate>
            <VoiceOnboardingPage />
          </AuthGate>
        }
      />

      {/* Buyer Network — DARK LAUNCH. Direct URL only, no nav links.
          Server allowlist gates it; non-allowlisted users bounce home. */}
      <Route
        path="/network"
        element={
          <AuthGate>
            <NetworkPage />
          </AuthGate>
        }
      />

      <Route
        path="/network/join"
        element={
          <AuthGate>
            <NetworkJoinPage />
          </AuthGate>
        }
      />

      {/* Legacy alias */}
      <Route path="/coverage" element={<Navigate to="/territories" replace />} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </ErrorBoundary>
  );
}


// Thin wrappers that pull profile + signOut from context and pass
// them to the page as props. Pages are kept prop-driven (rather
// than calling useAuth themselves) so they can be reused in
// non-authenticated contexts later (e.g. an admin preview tool).
function AuthenticatedTerritories() {
  const { profile, signOut } = useAuth();
  return <TerritoriesPage agent={profile} onSignOut={signOut} />;
}

function AuthenticatedBriefing() {
  const { profile, signOut } = useAuth();
  return <BriefingPage agent={profile} onSignOut={signOut} />;
}


// ── One-time voice onboarding nudge ────────────────────────────────
// A signed-in territory agent (role 'agent') who has never generated
// voice scripts is sent to /profile/voice once, the first time they
// land on a main app page. "Has a voice profile" is read from
// voice_onboarding_completed_at (set by the backend on any successful
// generation) or existing generated_scripts. "Once" is remembered per
// user in localStorage; if storage is unavailable, an in-memory set
// limits it to once per page load. Operators and buyer agents are not
// nudged. /zip/:zip is excluded so a first-time briefing provisioning
// poll (BriefingPage) is never interrupted.
const NUDGE_PATHS = ['/territories', '/my-leads', '/letters'];
const nudgedThisLoad = new Set();

function nudgeKey(userId) {
  return `ss.voiceOnboardingNudged.${userId}`;
}

function alreadyNudged(userId) {
  if (nudgedThisLoad.has(userId)) return true;
  try {
    return window.localStorage.getItem(nudgeKey(userId)) === '1';
  } catch {
    return false;
  }
}

function rememberNudged(userId) {
  nudgedThisLoad.add(userId);
  try {
    window.localStorage.setItem(nudgeKey(userId), '1');
  } catch {
    // Storage blocked: the in-memory set still prevents a loop.
  }
}

function VoiceOnboardingNudge() {
  const { session, profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!session || !profile?.id) return;
    if (profile.role !== 'agent') return;
    if (!NUDGE_PATHS.includes(location.pathname)) return;
    const hasScripts = profile.generated_scripts
      && typeof profile.generated_scripts === 'object'
      && Object.keys(profile.generated_scripts).length > 0;
    if (profile.voice_onboarding_completed_at || hasScripts) return;
    if (alreadyNudged(profile.id)) return;
    rememberNudged(profile.id);
    navigate('/profile/voice');
  }, [session, profile, location.pathname, navigate]);

  return null;
}
